import { FC, useContext, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import ErrorBoundary from "@app/shared/ErrorBoundary";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { Unauthorized } from "@app/shared/ErrorPages";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { UserContext } from "@app/user/UserContext";
import { Dashboard as NetworkPortfolioManagementDashboard } from "@app/network-portfolio-management/Dashboard";
import { ViewToggles } from "@app/network-portfolio-management/components/ViewToggles";
import { Toggle } from "@app/shared/Toggle";
import { NPM_DASHBOARD_VIEW_CLASSIC, NPM_DASHBOARD_VIEW_NEW } from "@app/shared/network-portfolio-management/constants";
import { isNetworkPortfolioManagementBeta, networkPortfolioManagementViewStorageKey } from "@app/shared/network-portfolio-management/utility";
import useIsNPMDashboardActive from "@app/network-portfolio-management/hooks/useIsNPMDashboardActive";
import { ClassicHome } from "@app/home/ClassicHome";
import { OutlineBadge, OutlineBadgeColor } from "@app/shared/OutlineBadge";

// isNPMActive is derived on every render from feature flags + localStorage so the correct view
// is shown immediately when flags settle, without a brief ClassicHome mount for NPM users.
// localStorage is the source of truth for the active view; the revision counter below exists
// only to trigger a re-render after localStorage is written by the toggle.
const Home: FC<RouteComponentProps> = (routeProps) => {
  const { enabledFeatureFlags, featureFlagsLoading } = useContext(FeatureFlagContext);
  const user = useContext(UserContext);

  const [, setNpmRevision] = useState(0);

  const isNPMDashboardActive = useIsNPMDashboardActive();

  const isNPMActive = !featureFlagsLoading && isNPMDashboardActive;

  const handleToggle = () => {
    if (!user) return;
    const newValue = !isNPMActive;
    localStorage.setItem(networkPortfolioManagementViewStorageKey(user), newValue ? NPM_DASHBOARD_VIEW_NEW : NPM_DASHBOARD_VIEW_CLASSIC);
    setNpmRevision((r) => r + 1);
  };

  if (featureFlagsLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Unauthorized />;
  }

  if (isNPMActive) {
    const showBetaBadge = isNetworkPortfolioManagementBeta(enabledFeatureFlags, user.roles);
    return (
      <ErrorBoundary user={user}>
        <Page documentTitle="Network Assessment">
          <PageInner>
            <PageHeader title="Project Dashboard" titleBadge={showBetaBadge ? <OutlineBadge color={OutlineBadgeColor.Blue} testId="npm-beta-badge-title">Beta</OutlineBadge> : undefined} disableHR={true}>
              <PageHeaderLeft>
                <ViewToggles />
              </PageHeaderLeft>
              <PageHeaderRight>
                <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN]}>
                  <Link to="/projects/create/1" className="qes-button qes-button-primary mt-1 mr5" type="button" data-testid="home_add-project">
                    <QesIcon iconName={QesIcons.Plus} isDecorative className="align-text-top me-1" /> Add Project...
                  </Link>
                </RoleRestricted>
                <span className="ml5 network-portfolio-management-toggle-wrapper">
                  <Toggle
                    active={true}
                    label={<>New QES Project Dashboard{showBetaBadge && <OutlineBadge color={OutlineBadgeColor.Blue} testId="npm-beta-badge-toggle">Beta</OutlineBadge>}</>}
                    labelSide="right"
                    onChange={handleToggle}
                    size="medium"
                    testId="network-portfolio-management-dashboard-toggle"
                  />
                </span>
              </PageHeaderRight>
            </PageHeader>
            <PageSection>
              <NetworkPortfolioManagementDashboard />
            </PageSection>
          </PageInner>
        </Page>
      </ErrorBoundary>
    );
  }

  return (
    <ClassicHome
      {...routeProps}
      onToggle={handleToggle}
    />
  );
};

export { Home };
